import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Facebook, MessageCircle, Phone, Send, LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

interface FloatingShareBarProps {
  title: string;
  excerpt?: string;
  url: string;
}

export function FloatingShareBar({ title, excerpt = '', url }: FloatingShareBarProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Show after scrolling past 400px
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const share = (platform: string) => {
    const shareUrls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title}\n${url}`)}`,
      viber: `viber://forward?text=${encodeURIComponent(`${title}\n${url}`)}`,
      x: `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      messenger: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? `fb-messenger://share/?link=${encodeURIComponent(url)}`
        : `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&redirect_uri=${encodeURIComponent(url)}`,
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success('Povezava kopirana!');
      return;
    }

    window.open(shareUrls[platform], '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const buttons = [
    { id: 'facebook', icon: Facebook, color: '#1877F2', label: 'Facebook' },
    { id: 'messenger', icon: MessageCircle, color: '#0099FF', label: 'Messenger' },
    { id: 'whatsapp', icon: MessageCircle, color: '#25D366', label: 'WhatsApp' },
    { id: 'viber', icon: Phone, color: '#7360F2', label: 'Viber' },
    { id: 'x', icon: XIcon, color: '#000000', label: 'X' },
    { id: 'telegram', icon: Send, color: '#0088cc', label: 'Telegram' },
    { id: 'copy', icon: LinkIcon, color: '#6b7280', label: 'Kopiraj' },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="fixed left-2 bottom-16 z-40 flex xl:hidden flex-row gap-1.5"
        >
          {buttons.map(({ id, icon: Icon, color }) => (
            <button
              key={id}
              onClick={() => share(id)}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-110"
              style={{ backgroundColor: color }}
              aria-label={`Deli na ${id}`}
            >
              {id === 'x' ? (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              ) : (
                <Icon className="h-4 w-4" />
              )}
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Placeholder so the X button uses the inline SVG instead
function XIcon() {
  return null;
}
